import React, { useState, useEffect, useRef } from "react";
import { Button, Collapse, Input, Tooltip, Tag } from "antd";
import type { TextAreaRef } from "antd/es/input/TextArea";
import { CloseCircleFilled } from "@ant-design/icons";
import PencilIcon from "@/assets/icons/edit.svg?react";
import VivoIcon from "@/assets/icons/vivo_logo_white.svg?react";
import AlertNotificationToggles, {
  type AlertNotificationChannel,
} from "../Alerts/AlertNotificationToggles";
import { ValidateResponse, EvaluationResponse } from "./insights";

const { TextArea } = Input;

type CreateAlertFormProps = {
  alertRule: string;
  alertName: string;
  onRuleChange: (rule: string) => void;
  isAlertRuleValid: boolean | null;
  handleValidateAlertRule: () => void;
  validateResponse: ValidateResponse | null;
  loadingValidation: boolean;
  alertEvaluation: EvaluationResponse | null;
  loadingEvaluation: boolean;
  loadingCreateAlert: boolean;
  errorAlert: boolean;
  handleCreateAlert: () => void;
  onEditRule: () => void;
  onEditAlertName: (value: string) => void;
  notifyEmail: boolean;
  notifyPush: boolean;
  onNotificationChange: (
    channel: AlertNotificationChannel,
    value: boolean,
  ) => void;
};

type ExplanationItem = {
  key: string;
  label: React.ReactNode;
  children?: React.ReactNode;
  styles?: {
    header?: React.CSSProperties;
    body?: React.CSSProperties;
  };
  showArrow?: boolean;
  disabled?: boolean;
};

export default function CreateAlertForm({
  alertRule,
  alertName,
  isAlertRuleValid,
  handleValidateAlertRule,
  alertEvaluation,
  validateResponse,
  loadingValidation,
  loadingEvaluation,
  loadingCreateAlert,
  errorAlert,
  handleCreateAlert,
  onRuleChange,
  onEditRule,
  onEditAlertName,
  notifyEmail,
  notifyPush,
  onNotificationChange,
}: CreateAlertFormProps) {
  const textAreaRef = useRef<TextAreaRef>(null);
  const [hasTypedARule, setHasTypedARule] = useState<boolean>(false);
  const [showAlertExplanation, setShowAlertExplanation] =
    useState<boolean>(false);

  const alertValidityExplanation =
    validateResponse?.condition_validity_pass_fail_reason?.trim();

  useEffect(() => {
    if (!isAlertRuleValid && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [isAlertRuleValid]);

  const handleRuleChange = (value: string) => {
    onRuleChange(value);
    const hasContent = value.trim() !== "";
    setHasTypedARule(hasContent);

    if (!hasContent) {
      setShowAlertExplanation(false);
    }
  };

  const handleEditClick = () => {
    onEditRule();
    setHasTypedARule(false);
    setShowAlertExplanation(false);
  };

  const handleTestAlert = () => {
    setShowAlertExplanation(true);
    handleValidateAlertRule();
  };

  const shouldShowSubmitButton =
    isAlertRuleValid === true && !loadingEvaluation && alertEvaluation !== null;

  const explanationItems = getExplanationItems(
    isAlertRuleValid,
    loadingValidation,
    alertValidityExplanation,
    loadingEvaluation,
    alertEvaluation,
  );

  return (
    <>
      <div className="flex-1 overflow-y-auto mb-5 ml-1 mr-2 hide-scrollbar">
        <div className="flex flex-col w-full mt-3 mb-2">
          {errorAlert && (
            <p className="text-xs text-red-600 font-semibold mb-2">
              Something went wrong while creating the alert. Please try again.
            </p>
          )}
          <HeaderSection />
          <AlertRuleInputSection
            alertRule={alertRule}
            isAlertRuleValid={isAlertRuleValid}
            loadingValidation={loadingValidation}
            loadingEvaluation={loadingEvaluation}
            explanationItems={explanationItems}
            showAlertExplanation={showAlertExplanation}
            handleRuleChange={handleRuleChange}
            handleEditClick={handleEditClick}
            handleTestAlert={handleTestAlert}
            hasTypedARule={hasTypedARule}
            textAreaRef={textAreaRef}
          />
        </div>
        {shouldShowSubmitButton && (
          <AlertNameSection
            alertName={alertName}
            onEditAlertName={onEditAlertName}
            loadingCreateAlert={loadingCreateAlert}
            handleCreateAlert={handleCreateAlert}
            notifyEmail={notifyEmail}
            notifyPush={notifyPush}
            onNotificationChange={onNotificationChange}
          />
        )}
      </div>
      {isAlertRuleValid === false && <FooterSection />}
    </>
  );
}

function HeaderSection() {
  return (
    <div className="flex flex-col w-full mt-3 mb-2">
      <div className="mb-2">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex justify-center items-center p-[8px_5px] w-[24px] h-[24px] bg-[#16252A] rounded-full">
            <VivoIcon />
          </div>
          <div className="text-sm text-[#2B4752] font-medium">
            Define a custom rule to trigger an alert from your insight context.
          </div>
        </div>
        <p className="text-xs text-[#00000073]">
          <strong>Example</strong>:{" "}
          <span className="italic"> Alert me if... </span>
          <span>
            {" "}
            “the number of enrolled patients drops below target” or “adverse
            events increase by more than 10%”
          </span>
        </p>
      </div>
    </div>
  );
}

type AlertRuleInputSectionProps = {
  alertRule: string;
  isAlertRuleValid: boolean | null;
  loadingValidation: boolean;
  loadingEvaluation: boolean;
  explanationItems: ExplanationItem[];
  showAlertExplanation: boolean;
  handleRuleChange: (value: string) => void;
  handleEditClick: () => void;
  handleTestAlert: () => void;
  hasTypedARule: boolean;
  textAreaRef: React.RefObject<TextAreaRef | null>;
};

function AlertRuleInputSection({
  alertRule,
  isAlertRuleValid,
  loadingValidation,
  loadingEvaluation,
  explanationItems,
  showAlertExplanation,
  handleRuleChange,
  handleEditClick,
  handleTestAlert,
  hasTypedARule,
  textAreaRef,
}: AlertRuleInputSectionProps) {
  return (
    <div className="">
      {/*Entry alert section */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <label className="block text-sm  text-[#2B4752]">Alert me if...</label>
      </div>

      {/* Alert Rule Entry */}
      <div className="relative mt-1 mb-2">
        {!loadingValidation &&
          !loadingEvaluation &&
          isAlertRuleValid !== null && (
            <Tooltip title="Edit your alert rule" placement="topRight">
              <span className="absolute right-2 bottom-2 z-10">
                <PencilIcon
                  data-testid="alert-rule-edit-button"
                  className="text-[#064C68] cursor-pointer"
                  onClick={handleEditClick}
                />
              </span>
            </Tooltip>
          )}

        <TextArea
          data-testid="alert-rule-textarea"
          ref={textAreaRef}
          id="alert-rule-textarea"
          value={alertRule}
          onChange={(e) => handleRuleChange(e.target.value)}
          placeholder="Enter your alert rule here..."
          rows={5}
          disabled={
            loadingValidation || loadingEvaluation || isAlertRuleValid !== null
          }
          className="bg-white"
          style={{ resize: "none" }}
        />
      </div>

      {/* Alert Test Explanation Section */}
      <div className="mt-4">
        {showAlertExplanation && hasTypedARule && (
          <div data-testid="alert-explanation-section">
            <AlertExplanationSection
              explanationItems={explanationItems}
              loadingValidation={loadingValidation}
            />
          </div>
        )}
      </div>

      {!loadingValidation &&
        (isAlertRuleValid === null || isAlertRuleValid === false) && (
          <div className="flex flex-start">
            <Button
              data-testid="test-my-alert-button"
              type="default"
              className="rounded-[4px] h-8 bg-[#D2EFF9] border-[#06709B] text-[#06709B] mt-1"
              onClick={handleTestAlert}
              disabled={!hasTypedARule || isAlertRuleValid === false}
            >
              Test my alert
            </Button>
          </div>
        )}
    </div>
  );
}

export function AlertTestLabel({
  isValid,
  loading = false,
}: {
  isValid: boolean | null;
  loading?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <div className="text-xs text-[#00000073] font-medium mt-1">
        ALERT TEST:
      </div>
      {loading && (
        <span className="text-[#06709B] shimmer-text">
          Checking alert validity...
        </span>
      )}
      {isValid === true && !loading && (
        <Tag color="#79A799" variant="solid">
          VALID
        </Tag>
      )}
      {isValid === false && !loading && (
        <Tag color="#980000" variant="solid">
          INVALID
        </Tag>
      )}
    </div>
  );
}

export function AlertStatusLabel({
  isMet,
  loading = false,
}: {
  isMet: boolean | null | undefined;
  loading?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <div className="text-xs text-[#00000073] font-medium mt-1">
        ALERT STATUS:
      </div>
      {loading && (
        <span className="text-[#06709B] shimmer-text">
          Evaluating alert status...
        </span>
      )}
      {isMet === true && !loading && (
        <Tag color="#991962" variant="solid">
          ALERTED
        </Tag>
      )}
      {isMet === false && !loading && (
        <Tag color="#D2EFF9" variant="solid">
          <span className="text-[#06709B]">MONITORED</span>
        </Tag>
      )}
    </div>
  );
}

type AlertExplanationSectionProps = {
  explanationItems: ExplanationItem[];
  loadingValidation: boolean;
  compact?: boolean;
  defaultCollapsed?: boolean;
};

export function AlertExplanationSection({
  explanationItems,
  loadingValidation,
  compact = false,
  defaultCollapsed = false,
}: AlertExplanationSectionProps) {
  const items = compact
    ? explanationItems.map((item) => ({
        ...item,
        styles: {
          header: { backgroundColor: "#fff" },
          body: { backgroundColor: "#fff" },
        },
      }))
    : explanationItems;

  return (
    <div className="mb-2">
      <Collapse
        size="small"
        className="insight-collapse mt-3"
        items={items}
        defaultActiveKey={loadingValidation || defaultCollapsed ? [] : ["1"]}
        expandIconPlacement="end"
      />
    </div>
  );
}

type AlertNameInputProps = {
  alertName: string;
  onChange: (value: string) => void;
};

export function AlertNameInput({ alertName, onChange }: AlertNameInputProps) {
  return (
    <>
      <div className="flex gap-2 mt-1 mb-1">
        <label className="block mb-1 text-sm">Alert name</label>
      </div>
      <TextArea
        data-testid="alert-name-input"
        value={alertName}
        showCount
        maxLength={150}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter alert name here"
        rows={3}
        style={{ resize: "none" }}
      />
    </>
  );
}

type AlertNameSectionProps = {
  alertName: string;
  onEditAlertName: (value: string) => void;
  loadingCreateAlert: boolean;
  handleCreateAlert: () => void;
  notifyEmail: boolean;
  notifyPush: boolean;
  onNotificationChange: (
    channel: AlertNotificationChannel,
    value: boolean,
  ) => void;
};

function AlertNameSection({
  alertName,
  onEditAlertName,
  loadingCreateAlert,
  handleCreateAlert,
  notifyEmail,
  notifyPush,
  onNotificationChange,
}: AlertNameSectionProps) {
  return (
    <>
      <AlertNameInput alertName={alertName} onChange={onEditAlertName} />
      <div className="mt-3">
        <label className="block mb-1 text-sm">Notifications</label>
        <AlertNotificationToggles
          notifyEmail={notifyEmail}
          notifyPush={notifyPush}
          onChange={onNotificationChange}
        />
      </div>
      <Button
        data-testid="alert-submit-button"
        type="primary"
        onClick={handleCreateAlert}
        className="bg-[#0C98D0] text-white mt-4"
        loading={loadingCreateAlert}
        disabled={!alertName || alertName.trim() === ""}
      >
        Submit
      </Button>
    </>
  );
}

function FooterSection() {
  return (
    <div
      data-testid="alert-invalid-footer"
      className="flex justify-center mb-6"
    >
      <div className="flex bg-[#ffd8d899] text-black p-2 text-xs rounded-md gap-2">
        <CloseCircleFilled className="text-[#980000]" />
        <p>Invalid alert. Please edit and retry.</p>
      </div>
    </div>
  );
}

function getExplanationItems(
  isAlertRuleValid: boolean | null,
  loadingValidation: boolean,
  alertValidityExplanation: string | undefined,
  loadingEvaluation: boolean,
  alertEvaluation: EvaluationResponse | null,
): ExplanationItem[] {
  const items = [
    {
      key: "1",
      label: (
        <AlertTestLabel
          isValid={isAlertRuleValid}
          loading={loadingValidation}
        />
      ),
      ...(alertValidityExplanation !== undefined &&
        !loadingValidation && {
          children: (
            <div className="text-xs text-[#2B4752]">
              {alertValidityExplanation ||
                "No additional explanation was provided."}
            </div>
          ),
        }),
      styles: {
        header: { backgroundColor: "#fff", borderRadius: "8px" },
        body: { backgroundColor: "#fff" },
      },
      showArrow: loadingValidation
        ? false
        : alertValidityExplanation !== undefined,
      disabled: loadingValidation,
    },
  ];

  if (
    isAlertRuleValid === true &&
    (loadingEvaluation || alertEvaluation !== null)
  ) {
    items.push({
      key: "2",
      label: (
        <AlertStatusLabel
          isMet={alertEvaluation?.condition_is_met}
          loading={loadingEvaluation}
        />
      ),
      ...(alertEvaluation?.condition_met_detail &&
        !loadingEvaluation && {
          children: (
            <div className="text-xs text-[#2B4752]">
              {alertEvaluation.condition_met_detail ||
                "No additional explanation was provided."}
            </div>
          ),
        }),
      styles: {
        header: { backgroundColor: "#fff", borderRadius: "8px" },
        body: { backgroundColor: "#fff" },
      },
      showArrow: loadingEvaluation
        ? false
        : !!alertEvaluation?.condition_met_detail,
      disabled: loadingEvaluation,
    });
  }
  return items;
}
